import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useApp } from "@/context/AppContext";

const COLORS = { green: "var(--demand-low)", yellow: "var(--demand-medium)", red: "var(--demand-high)" };

export default function DemandCalendar({ forecast, onSelect }) {
  const { lang, t } = useApp();
  const byDate = useMemo(() => Object.fromEntries(forecast.map((d) => [d.date, d])), [forecast]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startPad = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthName = cursor.toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { month: "long", year: "numeric" });
  const dow = lang === "it" ? ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"] : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const iso = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <div data-testid="demand-calendar" className="bg-card border border-border rounded-lg p-6 h-full">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold capitalize">{monthName}</h3>
        <div className="flex gap-1">
          <button data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-2 rounded-md hover:bg-accent"><ChevronLeft className="w-4 h-4" /></button>
          <button data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-2 rounded-md hover:bg-accent"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1.5 mb-2">
        {dow.map((d) => <div key={d} className="text-center text-xs font-bold uppercase tracking-wider text-muted-foreground">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const info = byDate[iso(d)];
          const bg = info ? COLORS[info.color] : "transparent";
          return (
            <button key={i} data-testid={info ? `cal-day-${iso(d)}` : undefined}
              disabled={!info} onClick={() => info && onSelect(info)}
              className="aspect-square rounded-md flex flex-col items-center justify-center text-xs relative transition-transform hover:-translate-y-0.5 disabled:opacity-30 disabled:hover:translate-y-0"
              style={info ? { backgroundColor: bg, color: "#fff" } : { border: "1px solid hsl(var(--border))" }}>
              <span className="font-semibold">{d}</span>
              {info && <span className="font-data text-[10px] opacity-90">€{info.recommended_price}</span>}
            </button>
          );
        })}
      </div>
      <div className="mt-6 pt-4 border-t border-border/60 text-xs text-muted-foreground space-y-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-semibold text-foreground">{lang === "it" ? "Colore = domanda prevista:" : "Color = forecast demand:"}</span>
          {[["green", t("lowD")], ["yellow", t("mediumD")], ["red", t("highD")]].map(([c, l]) => (
            <div key={c} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ backgroundColor: COLORS[c] }} />{l}</div>
          ))}
        </div>
        <p>
          <span className="font-semibold text-foreground">{lang === "it" ? "Prezzo mostrato:" : "Price shown:"}</span>{" "}
          {lang === "it"
            ? "è il prezzo CONSIGLIATO dall'AI (non quello che hai impostato). È la tariffa camera da impostare, ESCLUSA la tassa di soggiorno e PRIMA dei tuoi sconti/offerte (es. Genius/promo). Apri il giorno per vedere il lordo (con tassa) e il netto (dopo i tuoi sconti)."
            : "it's the AI RECOMMENDED price (not the one you set). It's the room rate to set, EXCLUDING city tax and BEFORE your discounts/offers (e.g. Genius/promo). Open a day to see gross (with tax) and net (after your discounts)."}
        </p>
      </div>
    </div>
  );
}
